import { Game } from './game/Game';
import { SocketClient } from './net/SocketClient';
import { isFromPortal, getPortalUsername } from './game/Portal';
import type { Role, Team } from '@shared/types';

// ============================================================
// Username handling
// ============================================================

function getSavedUsername(): string | null {
  return localStorage.getItem('skyfall_username');
}

function saveUsername(name: string): void {
  localStorage.setItem('skyfall_username', name);
}

// ============================================================
// Error display
// ============================================================

function showError(form: HTMLElement, message: string): void {
  const existing = document.getElementById('connect-error');
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.id = 'connect-error';
  el.style.cssText = 'color:#f44;font-size:14px;font-family:monospace;margin-top:4px;';
  el.textContent = message;
  form.appendChild(el);
}

function clearError(): void {
  const existing = document.getElementById('connect-error');
  if (existing) existing.remove();
}

// ============================================================
// Boot
// ============================================================

function boot(): void {
  const overlay = document.getElementById('username-overlay')!;
  const form = document.getElementById('username-form')!;
  const usernameInput = document.getElementById('username-input') as HTMLInputElement;
  const gamecodeInput = document.getElementById('gamecode-input') as HTMLInputElement;
  const submitBtn = document.getElementById('username-submit') as HTMLButtonElement;
  const hud = document.getElementById('hud')!;

  // Auto-launch if coming from a portal — skip lobby
  if (isFromPortal()) {
    const portalUsername = getPortalUsername() || getSavedUsername() || 'Pilot';
    overlay.style.display = 'none';
    hud.style.display = 'block';
    startGame(portalUsername, null).catch((err) => {
      console.error('[SKYFALL] Portal start failed:', err);
      // Fall back to lobby
      overlay.style.display = 'flex';
      hud.style.display = 'none';
    });
    return;
  }

  const saved = getSavedUsername();
  if (saved) {
    usernameInput.value = saved;
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const username = usernameInput.value.trim();
    if (username.length === 0) return;

    const gameCode = gamecodeInput.value.trim().toUpperCase();
    clearError();

    saveUsername(username);
    submitBtn.disabled = true;
    submitBtn.textContent = 'CONNECTING...';

    startGame(username, gameCode.length > 0 ? gameCode : null)
      .then(() => {
        overlay.style.display = 'none';
        hud.style.display = 'block';
      })
      .catch((err: Error) => {
        console.error('[SKYFALL] Failed to start:', err);
        submitBtn.disabled = false;
        submitBtn.textContent = 'LAUNCH';
        showError(form, err.message);
      });
  });

  usernameInput.focus();
}

// ============================================================
// Game initialization
// ============================================================

async function startGame(username: string, gameCode: string | null): Promise<void> {
  const socket = new SocketClient();

  // Connect to server
  try {
    await socket.connect();
  } catch {
    throw new Error('Connection failed — is the server running?');
  }

  // Join a game (server handles room routing)
  const joinResult = await socket.join(username, gameCode);

  if (!joinResult.ok) {
    socket.disconnect();
    throw new Error(joinResult.error ?? 'Failed to join game');
  }

  // Show the game code in settings overlay
  const codeEl = document.getElementById('game-code');
  if (codeEl && joinResult.gameCode) {
    codeEl.textContent = joinResult.gameCode;
  }

  // Wait for team assignment (with 10s timeout)
  const { team, role } = await Promise.race([
    new Promise<{ team: Team; role: Role }>((resolve) => {
      socket.on('game:teamAssigned', (assignedTeam, assignedRole) => {
        resolve({ team: assignedTeam, role: assignedRole });
      });
    }),
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Server did not assign a team — try again')), 10000);
    }),
  ]);

  console.log(`[SKYFALL] Assigned: ${role} on team ${team} (room: ${joinResult.gameCode})`);

  // Initialize game with networking
  const game = new Game();
  await game.init(role, username, socket, team);

  console.log(`[SKYFALL] Flying as ${role} — click to capture mouse, WASD to fly`);
}

// ============================================================
// Entry point
// ============================================================

document.addEventListener('DOMContentLoaded', boot);
